import {logger} from './logger';

export interface Requirement {
    label: string;
}

export interface DownloadCard {
    title: string;
    version: string;
    requirements: Requirement[];
    downloadUrl: string;
    platform: 'windows' | 'mac' | 'linux';
}

export interface ReleaseNote {
    version: string;
    date: string;
    items: string[];
}

export function parseDownloadCards(html: string): DownloadCard[] {
    if (!html) return [];

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const cards: DownloadCard[] = [];

        const downloadLinks = doc.querySelectorAll('a');

        downloadLinks.forEach((link) => {
            const text = link.textContent?.trim() || '';

            const platform = identifyPlatform(text);
            if (!platform) {
                return;
            }

            const versionMatch = text.match(/Version:\s*(\d+\.\d+)/i);
            const version = versionMatch ? versionMatch[1] : '';

            const requirements: Requirement[] = [];
            const lines = text.split('\n').map(l => l.trim()).filter(l => l);
            lines.forEach(line => {
                if (line && !line.toLowerCase().includes('version') && !line.toLowerCase().includes('for')) {
                    requirements.push({label: line});
                }
            });

            cards.push({
                title: `Polysun ${getPlatformName(platform)}`,
                version,
                requirements,
                downloadUrl: '',
                platform
            });
        });

        return cards;
    } catch (error) {
        logger.error('Failed to parse download cards:', error);
        return [];
    }
}

function getPlatformName(platform: string): string {
    switch (platform) {
        case 'windows':
            return 'Windows';
        case 'mac':
            return 'Mac';
        case 'linux':
            return 'Linux';
        default:
            return platform;
    }
}

export function parseReleaseNotes(html: string): ReleaseNote[] {
    if (!html) return [];

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const releaseNotes: ReleaseNote[] = [];

        const cardElements = doc.querySelectorAll('.card');

        cardElements.forEach(card => {
            const h3Element = card.querySelector('h3');
            if (!h3Element) return;

            const text = h3Element.textContent?.trim() || '';

            const headerMatch = text.match(/Polysun\s+(\d+\.\d+(?:\.\d+)?)\s*[–—-]\s*Release\s+Notes\s*[–—-]\s*(.+)/i);
            if (!headerMatch) {
                return;
            }

            const version = headerMatch[1];
            const date = headerMatch[2];

            if (text.includes('_Withdrawn_')) {
                return;
            }

            const items: string[] = [];
            const ulElement = card.querySelector('ul');

            if (ulElement) {
                const listItems = ulElement.querySelectorAll('li');
                listItems.forEach(li => {
                    const itemText = li.textContent?.trim();
                    if (itemText) {
                        items.push(itemText);
                    }
                });
            }

            if (items.length > 0) {
                releaseNotes.push({version, date, items});
            }
        });

        return releaseNotes;
    } catch (error) {
        logger.error('Failed to parse release notes:', error);
        return [];
    }
}

function identifyPlatform(text: string): 'windows' | 'mac' | 'linux' | null {
    const lowerText = text.toLowerCase();

    if (lowerText.includes('windows') || lowerText.includes('win')) {
        return 'windows';
    }
    if (lowerText.includes('mac') || lowerText.includes('osx') || lowerText.includes('macos')) {
        return 'mac';
    }
    if (lowerText.includes('linux')) {
        return 'linux';
    }

    return null;
}
